import * as React from 'react';
import * as Strings from './Strings';
import * as AnnotationTypes from './AnnotationTypes';
import * as Features from '../../shared/Features';
import { Translate } from '../../shared/LanguageService';
import { IComboBoxCustomValue } from './ComboBox';
import { IStorey } from './Types';
import {
    Button,
    ButtonGroup,
    ButtonToolbar,
    FormGroup,
    FormControl,
    ControlLabel,
    InputGroup,
    DropdownButton,
    MenuItem,
    Tooltip,
    OverlayTrigger
} from 'react-bootstrap';
import { ChromePicker } from 'react-color';
import CheckboxInputRaw from '../../components/common/CheckboxInputRaw';

interface IProps {
    flipHorizontal: (e: any) => void;
    flipVertical: (e: any) => void;
    toggleGrid: (e: any) => void;
    rotate: (angle: number) => void;
    onChange: (e: any) => void;
    deleteObject: (e: any) => void;
    deleteAllObjectsExceptTexts: () => void;
    onToolbarFontSizeChange: (size: number) => void;
    onToolbarStrokeWidthChange: (width: number) => void;
    onToolbarStrokeColorChange: (rgb: any) => void;
    isDrawing: boolean;
    isDrawingEllipse: boolean;
    isDrawingRectangle: boolean;
    isCropping: boolean;
    crop: (e: any) => void;
    comment: string;
    storeys: IStorey[];
    storey: number;
    customStorey: IComboBoxCustomValue;
    storeyIsSelected: boolean;
    isObjectSelected: boolean;
    fontSize: number;
    type: string;
    strokeWidth: number;
    strokeColor: any;
    showColorPicker: boolean;
    onShowColorPickerChanged: () => void;
    isColorPipette: boolean;
    isRotateFreely: boolean;
    isGridActive: boolean;
    user: any;
}

const PlanAnnotationToolbar = (props: IProps) => {
    const popover: any = {
        position: 'absolute',
        zIndex: '3'
    };

    const cover: any = {
        position: 'fixed',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px'
    };

    const colorButtonStyle: any = {
        background: `rgba(${props.strokeColor.r}, ${props.strokeColor.g}, ${props.strokeColor.b}, ${props.strokeColor.a})`
    };

    return (
        <div className="tools-container">
            <ButtonToolbar className="plan-annotation-toolbar">
                {Features.hasFeature(Features.CropDeliveryFile, props.user) &&
                    (props.type === AnnotationTypes.AnnotationNormal ||
                        props.type === AnnotationTypes.ExternalAnnotation ||
                        props.type === AnnotationTypes.DeliveryAnnotation ||
                        props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                        <ButtonGroup>
                            <div className="input-group crop-group">
                                <CheckboxInputRaw
                                    disabled={props.isRotateFreely}
                                    name={Strings.cropToolCheckboxName}
                                    checked={props.isCropping}
                                    onChange={props.onChange}
                                    icon="crop"
                                    label={Translate('plans/annotation/toolbar/cropping')}
                                />
                            </div>
                        </ButtonGroup>
                    )}
                <ButtonGroup>
                    {(props.type === AnnotationTypes.AnnotationNormal ||
                        props.type === AnnotationTypes.ExternalAnnotation ||
                        props.type === AnnotationTypes.DeliveryAnnotation ||
                        props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                        <CheckboxInputRaw
                            name={Strings.rotateFreelyName}
                            checked={props.isRotateFreely}
                            onChange={props.onChange}
                            icon="rotate_right"
                            label={
                                props.isRotateFreely
                                    ? Translate('plans/annotation/toolbar/rotate-freely-apply')
                                    : Translate('plans/annotation/toolbar/rotate-freely')
                            }
                        />
                    )}
                    {props.isRotateFreely && (
                        <Button disabled={!props.isRotateFreely} onClick={props.toggleGrid}>
                            <i className="material-icons">
                                {props.isGridActive ? 'grid_off' : 'grid_on'}
                            </i>
                            {Translate('plans/annotation/toolbar/grid-button')}
                        </Button>
                    )}
                    <Button disabled={props.isRotateFreely} onClick={() => props.rotate(90)}>
                        <i className="material-icons flipped-icon-x">rotate_90_degrees_ccw</i> 90
                    </Button>
                    <Button disabled={props.isRotateFreely} onClick={() => props.rotate(180)}>
                        <i className="material-icons flipped-icon-x">rotate_90_degrees_ccw</i> 180
                    </Button>
                </ButtonGroup>
                <ButtonGroup>
                    <Button disabled={props.isRotateFreely} onClick={props.flipHorizontal}>
                        <i className="material-icons">swap_horiz</i>
                        {Translate('plans/annotation/toolbar/flipping')}
                    </Button>
                    <Button disabled={props.isRotateFreely} onClick={props.flipVertical}>
                        <i className="material-icons">swap_vert</i>
                        {Translate('plans/annotation/toolbar/flipping')}
                    </Button>
                </ButtonGroup>
            </ButtonToolbar>
            <ButtonToolbar className="plan-annotation-toolbar">
                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <ButtonGroup>
                        <CheckboxInputRaw
                            disabled={props.isRotateFreely}
                            name={Strings.drawToolCheckboxName}
                            checked={props.isDrawing}
                            onChange={props.onChange}
                            icon="create"
                            label={Translate('plans/annotation/toolbar/drawing')}
                        />
                    </ButtonGroup>
                )}
                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <ButtonGroup>
                        <CheckboxInputRaw
                            disabled={props.isRotateFreely}
                            name={Strings.drawEllipseToolCheckboxName}
                            checked={props.isDrawingEllipse}
                            onChange={props.onChange}
                            icon="ellipse"
                            label={Translate('plans/annotation/toolbar/ellipse')}
                        />
                    </ButtonGroup>
                )}
                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <ButtonGroup>
                        <CheckboxInputRaw
                            disabled={props.isRotateFreely}
                            name={Strings.drawRectangleToolCheckboxName}
                            checked={props.isDrawingRectangle}
                            onChange={props.onChange}
                            icon="rect"
                            label={Translate('plans/annotation/toolbar/rectangle')}
                        />
                    </ButtonGroup>
                )}
                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <FormGroup className="toolbar">
                        <InputGroup>
                            <ControlLabel>
                                {Translate('plans/annotation/toolbar/stroke-width')}
                            </ControlLabel>
                            <FormControl
                                type="range"
                                value={props.strokeWidth.toString()}
                                name={Strings.strokeWidthToolChange}
                                onChange={props.onChange}
                                min={1}
                                max={30}
                            />
                        </InputGroup>
                    </FormGroup>
                )}
                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <FormGroup className="toolbar">
                        <InputGroup>
                            <CheckboxInputRaw
                                name={Strings.colorPipetteToolCheckboxName}
                                checked={props.isColorPipette}
                                onChange={props.onChange}
                                icon="colorize"
                                label={''}
                            />
                        </InputGroup>
                    </FormGroup>
                )}
                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <FormGroup>
                        <InputGroup>
                            <Button
                                style={colorButtonStyle}
                                onClick={props.onShowColorPickerChanged}
                            ></Button>
                            {props.showColorPicker && (
                                <div style={popover}>
                                    <div style={cover} onClick={props.onShowColorPickerChanged} />
                                    <ChromePicker
                                        color={props.strokeColor}
                                        onChange={(color: any) =>
                                            props.onToolbarStrokeColorChange(color.rgb)
                                        }
                                    />
                                </div>
                            )}
                        </InputGroup>
                    </FormGroup>
                )}

                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.AnnotationTexts ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <FormGroup className="font-size-form toolbar">
                        <InputGroup>
                            <FormControl
                                type="number"
                                title="Font Size"
                                value={props.fontSize.toString()}
                                name={Strings.fontSizeToolChange}
                                placeholder="Font Size"
                                onChange={props.onChange}
                                disabled={
                                    props.isDrawing ||
                                    props.isDrawingEllipse ||
                                    props.isDrawingRectangle ||
                                    props.isCropping
                                }
                            />
                            <DropdownButton
                                id="font-size-dropdown"
                                title=""
                                disabled={
                                    props.isDrawing ||
                                    props.isDrawingEllipse ||
                                    props.isDrawingRectangle ||
                                    props.isCropping
                                }
                                componentClass={InputGroup.Button}
                            >
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(10)}
                                    key="10"
                                >
                                    10
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(11)}
                                    key="11"
                                >
                                    11
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(12)}
                                    key="12"
                                >
                                    12
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(14)}
                                    key="14"
                                >
                                    14
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(18)}
                                    key="18"
                                >
                                    18
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(24)}
                                    key="24"
                                >
                                    24
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(30)}
                                    key="30"
                                >
                                    30
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(36)}
                                    key="36"
                                >
                                    36
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(48)}
                                    key="48"
                                >
                                    48
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(60)}
                                    key="60"
                                >
                                    60
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(72)}
                                    key="72"
                                >
                                    72
                                </MenuItem>
                                <MenuItem
                                    onClick={() => props.onToolbarFontSizeChange(96)}
                                    key="96"
                                >
                                    96
                                </MenuItem>
                            </DropdownButton>
                        </InputGroup>
                    </FormGroup>
                )}
                <Button
                    bsStyle="danger"
                    name={Strings.trashButton}
                    className={Strings.trashButton}
                    disabled={
                        props.isDrawing ||
                        props.isDrawingEllipse ||
                        props.isDrawingRectangle ||
                        props.isCropping ||
                        !props.isObjectSelected ||
                        props.isRotateFreely
                    }
                    onClick={props.deleteObject}
                >
                    <i className="material-icons">delete</i>
                </Button>
                {(props.type === AnnotationTypes.AnnotationNormal ||
                    props.type === AnnotationTypes.ExternalAnnotation ||
                    props.type === AnnotationTypes.DeliveryAnnotation ||
                    props.type === AnnotationTypes.ExternalDeliveryAnnotation) && (
                    <Button
                        bsStyle="danger"
                        className={Strings.trashButton}
                        disabled={
                            props.isDrawing ||
                            props.isDrawingEllipse ||
                            props.isDrawingRectangle ||
                            props.isCropping ||
                            props.isRotateFreely
                        }
                        onClick={() => props.deleteAllObjectsExceptTexts()}
                    >
                        <i className="material-icons">delete</i>{' '}
                        {Translate('plans/annotation/toolbar/delete-all-except-texts')}
                    </Button>
                )}
            </ButtonToolbar>
        </div>
    );
};

export default PlanAnnotationToolbar;
